import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ShieldCheck, MessageCircle, Gift, Phone } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = [
  "Unternehmensgründung",
  "Immigration & Visum (E-2, EB-2 NIW, EB-1C, L-1A)",
  "Fixfertige Business-Idee",
  "Relocation & Umzug",
  "Beratung nach der Auswanderung",
  "Noch unsicher / Sonstiges",
];

const TIME_WINDOWS = [
  "Vormittag (9–12 Uhr)",
  "Nachmittag (13–17 Uhr)",
  "Abend (nach 17 Uhr)",
];

const inputCls =
  "h-12 rounded-none border-line bg-transparent text-ink placeholder:text-ink-muted/60 focus-visible:ring-1 focus-visible:ring-clay";
const textareaCls =
  "min-h-[120px] rounded-none border-line bg-transparent text-ink placeholder:text-ink-muted/60 focus-visible:ring-1 focus-visible:ring-clay";

function TopicSelect({ value, onChange, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={inputCls} data-testid={testId}>
        <SelectValue placeholder="Ihr Anliegen wählen" />
      </SelectTrigger>
      <SelectContent className="rounded-none border-line bg-sand">
        {TOPICS.map((topic) => (
          <SelectItem key={topic} value={topic} data-testid={`${testId}-option`}>
            {topic}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function SuccessNote({ testId, children }) {
  return (
    <div
      className="flex items-start gap-3 border border-clay/40 bg-sand-accent p-5 text-sm text-ink"
      data-testid={testId}
      role="status"
    >
      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-clay" />
      <p>{children}</p>
    </div>
  );
}

function BookingForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_date: "",
    preferred_time: "",
    topic: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/consultation`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        preferred_date: form.preferred_date || null,
        preferred_time: form.preferred_time || null,
        topic: form.topic || null,
        message: form.message || null,
      });
      setSent(true);
      toast.success("Anfrage gesendet – wir melden uns persönlich bei Ihnen.");
    } catch (err) {
      toast.error("Das hat leider nicht geklappt. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <SuccessNote testId="booking-success-message">
        Vielen Dank, {form.name.split(" ")[0]}! Ihre Anfrage für ein kostenloses
        Erstgespräch ist bei uns eingegangen. Wir melden uns persönlich bei
        Ihnen, um den Termin zu bestätigen.
      </SuccessNote>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="booking-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="booking-name">Name *</Label>
          <Input
            id="booking-name"
            data-testid="booking-name-input"
            required
            minLength={2}
            value={form.name}
            onChange={set("name")}
            placeholder="Ihr Name"
            className={inputCls}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="booking-email">E-Mail *</Label>
          <Input
            id="booking-email"
            type="email"
            data-testid="booking-email-input"
            required
            value={form.email}
            onChange={set("email")}
            placeholder="ihre@email.de"
            className={inputCls}
          />
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="booking-phone">Telefon (optional)</Label>
          <Input
            id="booking-phone"
            type="tel"
            data-testid="booking-phone-input"
            value={form.phone}
            onChange={set("phone")}
            placeholder="+41 / +49 …"
            className={inputCls}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="booking-date">Wunschtermin (optional)</Label>
          <Input
            id="booking-date"
            type="date"
            data-testid="booking-date-input"
            value={form.preferred_date}
            onChange={set("preferred_date")}
            className={inputCls}
          />
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Bevorzugte Zeit (optional)</Label>
          <Select value={form.preferred_time} onValueChange={set("preferred_time")}>
            <SelectTrigger className={inputCls} data-testid="booking-time-select">
              <SelectValue placeholder="Tageszeit wählen" />
            </SelectTrigger>
            <SelectContent className="rounded-none border-line bg-sand">
              {TIME_WINDOWS.map((t) => (
                <SelectItem key={t} value={t} data-testid="booking-time-option">
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Thema (optional)</Label>
          <TopicSelect
            value={form.topic}
            onChange={set("topic")}
            testId="booking-topic-select"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="booking-message">Ihre Nachricht (optional)</Label>
        <Textarea
          id="booking-message"
          data-testid="booking-message-input"
          value={form.message}
          onChange={set("message")}
          placeholder="Worum geht es bei Ihrer Auswanderung?"
          className={textareaCls}
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="booking-submit-button"
        className="inline-flex w-full items-center justify-center gap-3 bg-clay px-8 py-4 text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-clay-dark disabled:opacity-60 sm:w-auto"
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        Erstgespräch anfragen
      </button>
      <p className="text-xs leading-relaxed text-ink-muted/70">
        Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Daten gemäss{" "}
        <a
          href="/datenschutz"
          data-testid="booking-privacy-link"
          className="underline decoration-line underline-offset-2 transition-colors hover:text-clay"
        >
          Datenschutzerklärung
        </a>{" "}
        zu.
      </p>
    </form>
  );
}

function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    topic: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        topic: form.topic,
        message: form.message,
      });
      setSent(true);
      toast.success("Nachricht gesendet – wir melden uns persönlich bei Ihnen.");
    } catch (err) {
      toast.error("Das hat leider nicht geklappt. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <SuccessNote testId="contact-success-message">
        Vielen Dank, {form.name.split(" ")[0]}! Ihre Nachricht ist bei uns
        eingegangen. Wir melden uns persönlich bei Ihnen zurück.
      </SuccessNote>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="contact-name">Name *</Label>
          <Input
            id="contact-name"
            data-testid="contact-name-input"
            required
            minLength={2}
            value={form.name}
            onChange={set("name")}
            placeholder="Ihr Name"
            className={inputCls}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact-email">E-Mail *</Label>
          <Input
            id="contact-email"
            type="email"
            data-testid="contact-email-input"
            required
            value={form.email}
            onChange={set("email")}
            placeholder="ihre@email.de"
            className={inputCls}
          />
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="contact-phone">Telefon (optional)</Label>
          <Input
            id="contact-phone"
            type="tel"
            data-testid="contact-phone-input"
            value={form.phone}
            onChange={set("phone")}
            placeholder="+41 / +49 …"
            className={inputCls}
          />
        </div>
        <div className="space-y-2">
          <Label>Ihr Anliegen *</Label>
          <TopicSelect
            value={form.topic}
            onChange={set("topic")}
            testId="contact-topic-select"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact-message">Ihre Nachricht *</Label>
        <Textarea
          id="contact-message"
          data-testid="contact-message-input"
          required
          minLength={5}
          value={form.message}
          onChange={set("message")}
          placeholder="Erzählen Sie uns kurz von Ihrem Vorhaben …"
          className={textareaCls}
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="contact-submit-button"
        className="inline-flex w-full items-center justify-center gap-3 bg-clay px-8 py-4 text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-clay-dark disabled:opacity-60 sm:w-auto"
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        Nachricht senden
      </button>
      <p className="text-xs leading-relaxed text-ink-muted/70">
        Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Daten gemäss{" "}
        <a
          href="/datenschutz"
          data-testid="contact-privacy-link"
          className="underline decoration-line underline-offset-2 transition-colors hover:text-clay"
        >
          Datenschutzerklärung
        </a>{" "}
        zu.
      </p>
    </form>
  );
}

export default function Contact() {
  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="border-t border-line bg-sand-deep py-24 lg:py-32"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.3em] text-clay"
            data-testid="contact-eyebrow"
          >
            Kontakt
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-6 font-serif text-4xl leading-tight tracking-tight text-ink sm:text-5xl"
            data-testid="contact-title"
          >
            Sprechen wir über Ihren{" "}
            <span className="italic text-clay">Neustart</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 max-w-md text-base leading-relaxed text-ink-muted"
            data-testid="contact-intro"
          >
            Erzählen Sie uns von Ihrem Vorhaben – oder buchen Sie direkt Ihr
            kostenloses Erstgespräch. Wir antworten persönlich und auf Deutsch.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-10 space-y-3"
            data-testid="contact-direct"
          >
            <a
              href="tel:+13055294140"
              data-testid="contact-phone-link"
              className="group flex items-center gap-4 border border-line bg-sand px-5 py-4 transition-colors hover:border-clay"
            >
              <Phone className="h-5 w-5 shrink-0 text-clay" strokeWidth={1.5} />
              <span>
                <span className="block text-xs uppercase tracking-[0.15em] text-ink-muted">
                  Direkt anrufen
                </span>
                <span className="block text-base text-ink transition-colors group-hover:text-clay">
                  +1 305 529 4140
                </span>
              </span>
            </a>
          </motion.div>
          <motion.ul
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-12 space-y-6"
            data-testid="contact-promises"
          >
            {[
              { icon: Gift, text: "Kostenlos & unverbindlich" },
              { icon: MessageCircle, text: "Persönlich & auf Deutsch" },
              { icon: ShieldCheck, text: "Vertrauliche Behandlung Ihrer Daten" },
            ].map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-4 text-sm text-ink md:text-base">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-line bg-sand">
                  <Icon className="h-4 w-4 text-clay" strokeWidth={1.5} />
                </span>
                {text}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="border border-line bg-sand p-8 sm:p-12 lg:col-span-7"
        >
          <Tabs defaultValue="erstgespraech" data-testid="contact-tabs">
            <TabsList className="grid w-full grid-cols-2 rounded-none border border-line bg-sand-deep p-1">
              <TabsTrigger
                value="erstgespraech"
                data-testid="tab-trigger-erstgespraech"
                className="rounded-none text-xs uppercase tracking-[0.15em] data-[state=active]:bg-clay data-[state=active]:text-sand"
              >
                Erstgespräch buchen
              </TabsTrigger>
              <TabsTrigger
                value="nachricht"
                data-testid="tab-trigger-nachricht"
                className="rounded-none text-xs uppercase tracking-[0.15em] data-[state=active]:bg-clay data-[state=active]:text-sand"
              >
                Nachricht senden
              </TabsTrigger>
            </TabsList>
            <TabsContent value="erstgespraech" className="pt-8">
              <BookingForm />
            </TabsContent>
            <TabsContent value="nachricht" className="pt-8">
              <ContactForm />
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </section>
  );
}
